"use client"

import { useEffect, useId, useMemo } from "react"
import { pluralize } from "@/utils/text-helpers"
import { zodResolver } from "@hookform/resolvers/zod"
import { AssignmentSelectField } from "@ui/forms/fields/assignment-select-field"
import { ComboboxField } from "@ui/forms/fields/combobox-field"
import { Form, FormField } from "@ui/shad"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { isAllBooksId } from "@/lib/all-books"
import { useFetchProjectTemplates } from "@/hooks/api/project-templates/queries"
import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { useBookId } from "@/hooks/use-book-id"
import { SimpleFormDialog } from "@/components/dialogs"

const bulkAddToProjectSchema = z.object({
  project_template_id: z.string().min(1, "Project type is required"),
  assignment_id: z.string().min(1, "Assignee is required"),
})

export type BulkAddToProjectFormValues = z.infer<typeof bulkAddToProjectSchema>

interface BulkAddToProjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (projectTemplateId: string, assignedToId: string | null, assignedTeamId: string | null) => void
  isSubmitting: boolean
  selectedHouseholdCount: number
}

export function BulkAddToProjectDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  selectedHouseholdCount,
}: BulkAddToProjectDialogProps) {
  const dialogFormId = useId()

  const form = useForm<BulkAddToProjectFormValues>({
    resolver: zodResolver(bulkAddToProjectSchema),
    defaultValues: {
      project_template_id: "",
      assignment_id: "",
    },
  })

  const { data: projectTemplatesResponse, isLoading: isLoadingTemplates } = useFetchProjectTemplates()
  const { data: currentUser } = useFetchCurrentUser()
  const bookId = useBookId()
  const isAllBooks = isAllBooksId(bookId)

  const projectTemplateId = form.watch("project_template_id")
  const assignmentId = form.watch("assignment_id")

  useEffect(() => {
    if (!open) {
      form.reset({
        project_template_id: "",
        assignment_id: currentUser?.id ? `user:${currentUser.id}` : "",
      })
    }
  }, [form, open, currentUser?.id])

  useEffect(() => {
    if (currentUser?.id && !assignmentId) {
      form.setValue("assignment_id", `user:${currentUser.id}`, { shouldDirty: false })
    }
  }, [assignmentId, currentUser?.id, form])

  const projectTemplateOptions = useMemo(
    () =>
      (projectTemplatesResponse?.project_templates ?? [])
        .filter((template) => template.projectable_kind === "household")
        .map((template) => ({
          value: template.id,
          label: isAllBooks
            ? `${template.title} (${template.company_wide ? "All books" : template.books.map((book) => book.name).join(", ")})`
            : template.title,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [isAllBooks, projectTemplatesResponse?.project_templates]
  )

  const isConfirmDisabled =
    isSubmitting || isLoadingTemplates || projectTemplateOptions.length === 0 || !projectTemplateId || !assignmentId

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return
    onOpenChange(nextOpen)
  }

  const handleSubmit = form.handleSubmit((values) => {
    onConfirm(
      values.project_template_id,
      values.assignment_id.startsWith("user:") ? values.assignment_id.slice(5) : null,
      values.assignment_id.startsWith("team:") ? values.assignment_id.slice(5) : null
    )
  })

  return (
    <SimpleFormDialog
      title={<>Add {pluralize(selectedHouseholdCount, "household")} to a project</>}
      open={open}
      onOpenChange={handleOpenChange}
      action={{
        label: selectedHouseholdCount === 1 ? "Start project" : "Start projects",
        form: `${dialogFormId}-dialog-bulk-add-to-project-dialog-110`,
        disabled: isConfirmDisabled,
        pending: isSubmitting,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: isSubmitting }}
    >
      <Form {...form}>
        <form
          id={`${dialogFormId}-dialog-bulk-add-to-project-dialog-110`}
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-2"
        >
          <>
            <FormField
              control={form.control}
              name="project_template_id"
              render={({ field }) => (
                <ComboboxField
                  field={field}
                  id="project-template-selector"
                  label="Project type"
                  placeholder={isLoadingTemplates ? "Loading project types..." : "Search project types..."}
                  disabled={isLoadingTemplates || projectTemplateOptions.length === 0}
                  options={isLoadingTemplates ? [] : projectTemplateOptions}
                  emptyText="No project types found."
                />
              )}
            />

            <FormField
              control={form.control}
              name="assignment_id"
              render={({ field }) => <AssignmentSelectField id="project-owner" field={field} />}
            />

            <p className="text-muted-foreground text-sm">
              {isAllBooks
                ? "Company-wide project types work across books. Book-specific project types require every selected household to be in that book."
                : "Projects will use the default household and project type naming."}
            </p>
          </>
        </form>
      </Form>
    </SimpleFormDialog>
  )
}
